import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, catchError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { CriteriaSearchRequest, CriteriaFilter, CriteriaOrder } from './models/criteria.model';
import { UuidGeneratorService } from '@app/services/uuid-generator.service';

// Feedback types
export type FeedbackType = 'POSITIVE' | 'NEGATIVE' | 'SUGGESTION' | 'BUG_REPORT';
export type FeedbackStatus = 'PENDING' | 'REVIEWED' | 'RESOLVED' | 'DISMISSED';
export type FeedbackCategory = string; // Dynamic categories from entity types

// Entity type is now dynamic (string) - configured via API
export type FeedbackEntityType = string;

// API Request/Response interfaces
export interface CreateFeedbackRequest {
  id: string;
  entity_type: FeedbackEntityType;
  entity_id: string;
  feedback_type: FeedbackType;
  content: string;
  category?: FeedbackCategory;
  metadata?: Record<string, any>;
}

export interface FeedbackSearchRequest {
  filters: {
    field: string;
    operator: string;
    value: any;
  }[];
  order?: {
    field: string;
    direction: 'asc' | 'desc';
  };
  limit: number;
  offset: number;
}

export interface FeedbackApiResponse {
  id: string;
  entity_type: FeedbackEntityType;
  entity_id: string;
  feedback_type: FeedbackType;
  category?: FeedbackCategory;
  content: string;
  metadata?: Record<string, any>;
  user_id: string;
  user_name: string;
  user_email?: string;
  status: FeedbackStatus;
  created_at: string;
  sent_to?: string[];
}

export interface FeedbackSearchResponse {
  data: FeedbackApiResponse[];
  total: number;
  offset: number;
  limit: number;
}

export interface FeedbackStats {
  total: number;
  by_type: Record<FeedbackType, number>;
  by_status: Record<FeedbackStatus, number>;
  by_entity_type: Record<string, number>;
}

// Entity Type interfaces
export interface FeedbackEntityTypeConfig {
  id: string;
  name: string;
  categories: string[];
}

export interface FeedbackEntityTypesResponse {
  data: FeedbackEntityTypeConfig[];
  total: number;
  offset: number;
  limit: number;
}

export interface EntityTypeSearchRequest {
  filters: {
    field: string;
    operator: string;
    value: any;
  }[];
  order?: {
    field: string;
    direction: 'asc' | 'desc';
  } | null;
  limit: number;
  offset: number;
}

export interface CreateEntityTypeRequest {
  name: string;
  categories?: string[];
}

// Internal model
export interface Feedback {
  id: string;
  entityType: FeedbackEntityType;
  entityId: string;
  feedbackType: FeedbackType;
  category?: FeedbackCategory;
  content: string;
  metadata?: Record<string, any>;
  userId: string;
  userName: string;
  userEmail?: string;
  status: FeedbackStatus;
  createdAt: string;
  sentTo?: string[];
}

// Router interfaces
export interface FeedbackRouter {
  id: string;
  name: string;
  description: string;
}

export interface FeedbackRoutersResponse {
  data: FeedbackRouter[];
  total: number;
  offset: number;
  limit: number;
}

export interface FeedbackRouterAssignment {
  id: string;
  router_id: string;
  router_name: string;
  entity_type: string;
  category: string | null;
}

export interface FeedbackRouterAssignmentsResponse {
  data: FeedbackRouterAssignment[];
  total: number;
  offset: number;
  limit: number;
}

export interface CreateRouterAssignmentRequest {
  router_id: string;
  entity_type: string;
  category?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class FeedbackService {
  private readonly http = inject(HttpClient);
  private readonly uuidGenerator = inject(UuidGeneratorService);
  private readonly baseUrl = `${environment.yuloadApiUrl}/api`;

  /**
   * Create a new feedback
   */
  createFeedback(request: {
    entityType: FeedbackEntityType;
    entityId: string;
    feedbackType: FeedbackType;
    content: string;
    category?: FeedbackCategory;
    metadata?: Record<string, any>;
  }): Observable<{ id: string; message: string }> {
    const body: CreateFeedbackRequest = {
      id: this.uuidGenerator.generate(),
      entity_type: request.entityType,
      entity_id: request.entityId,
      feedback_type: request.feedbackType,
      content: request.content,
      category: request.category,
      metadata: {
        ...request.metadata,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        timestamp: new Date().toISOString(),
      },
    };

    return this.http.post<{ id: string; message: string }>(`${this.baseUrl}/feedback`, body).pipe(
      catchError((error) => {
        console.error('Error creating feedback:', error);
        throw error;
      }),
    );
  }

  /**
   * Search feedback with filters
   */
  searchFeedback(
    request: {
      filters?: { field: string; operator: string; value: any }[];
      order?: { field: string; direction: 'asc' | 'desc' };
      limit?: number;
      offset?: number;
    } = {},
  ): Observable<{ items: Feedback[]; total: number; offset: number; limit: number }> {
    const body: FeedbackSearchRequest = {
      filters: request.filters || [],
      order: request.order || { field: 'created_at', direction: 'desc' },
      limit: request.limit ?? 1000000,
      offset: request.offset ?? 0,
    };

    return this.http.post<FeedbackSearchResponse>(`${this.baseUrl}/feedback/search`, body).pipe(
      map((response) => ({
        items: response.data.map((item) => this.transformApiResponse(item)),
        total: response.total,
        offset: response.offset,
        limit: response.limit,
      })),
      catchError((error) => {
        console.error('Error searching feedback:', error);
        throw error;
      }),
    );
  }

  /**
   * Get feedback by ID
   */
  getFeedback(feedbackId: string): Observable<Feedback> {
    return this.http.get<FeedbackApiResponse>(`${this.baseUrl}/feedback/${feedbackId}`).pipe(
      map((response) => this.transformApiResponse(response)),
      catchError((error) => {
        console.error('Error getting feedback:', error);
        throw error;
      }),
    );
  }

  /**
   * Update feedback status
   */
  updateFeedbackStatus(feedbackId: string, status: FeedbackStatus): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${this.baseUrl}/feedback/${feedbackId}/status`, { status }).pipe(
      catchError((error) => {
        console.error('Error updating feedback status:', error);
        throw error;
      }),
    );
  }

  /**
   * Get feedback statistics
   */
  getStats(params?: { entityType?: string; entityId?: string }): Observable<FeedbackStats> {
    let httpParams = new HttpParams();

    if (params?.entityType) {
      httpParams = httpParams.set('entity_type', params.entityType);
    }
    if (params?.entityId) {
      httpParams = httpParams.set('entity_id', params.entityId);
    }

    return this.http.get<FeedbackStats>(`${this.baseUrl}/feedback/stats`, { params: httpParams }).pipe(
      catchError((error) => {
        console.error('Error getting feedback stats:', error);
        throw error;
      }),
    );
  }

  /**
   * Get feedback for a specific entity
   */
  getFeedbackForEntity(entityType: FeedbackEntityType, entityId: string): Observable<Feedback[]> {
    return this.searchFeedback({
      filters: [
        { field: 'entity_type', operator: 'equal', value: entityType },
        { field: 'entity_id', operator: 'equal', value: entityId },
      ],
      order: { field: 'created_at', direction: 'desc' },
      limit: 1000000,
    }).pipe(map((response) => response.items));
  }

  /**
   * Transform API response to internal model
   */
  private transformApiResponse(apiResponse: FeedbackApiResponse): Feedback {
    return {
      id: apiResponse.id,
      entityType: apiResponse.entity_type,
      entityId: apiResponse.entity_id,
      feedbackType: apiResponse.feedback_type,
      category: apiResponse.category,
      content: apiResponse.content,
      metadata: apiResponse.metadata,
      userId: apiResponse.user_id,
      userName: apiResponse.user_name,
      userEmail: apiResponse.user_email,
      status: apiResponse.status,
      createdAt: apiResponse.created_at,
      sentTo: apiResponse.sent_to,
    };
  }

  // ===== Entity Types API =====

  /**
   * Search entity types with Criteria format
   */
  searchEntityTypes(
    request: {
      filters?: { field: string; operator: string; value: any }[];
      order?: { field: string; direction: 'asc' | 'desc' } | null;
      limit?: number;
      offset?: number;
    } = {},
  ): Observable<{ items: FeedbackEntityTypeConfig[]; total: number }> {
    const body: EntityTypeSearchRequest = {
      filters: request.filters || [],
      order: request.order || null,
      limit: request.limit ?? 1000000,
      offset: request.offset ?? 0,
    };

    return this.http.post<FeedbackEntityTypesResponse>(`${this.baseUrl}/feedback-entity-types/search`, body).pipe(
      map((response) => ({
        items: response.data,
        total: response.total,
      })),
      catchError((error) => {
        console.error('Error searching entity types:', error);
        throw error;
      }),
    );
  }

  /**
   * Get all entity types with their categories
   */
  getEntityTypes(): Observable<FeedbackEntityTypeConfig[]> {
    return this.searchEntityTypes({ limit: 1000000 }).pipe(map((response) => response.items));
  }

  /**
   * Create a new entity type
   */
  createEntityType(request: CreateEntityTypeRequest): Observable<{ id: string; message: string }> {
    return this.http.post<{ id: string; message: string }>(`${this.baseUrl}/feedback-entity-types`, request).pipe(
      catchError((error) => {
        console.error('Error creating entity type:', error);
        throw error;
      }),
    );
  }

  /**
   * Delete an entity type
   */
  deleteEntityType(id: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/feedback-entity-types/${id}`).pipe(
      catchError((error) => {
        console.error('Error deleting entity type:', error);
        throw error;
      }),
    );
  }

  // ===== Routers API =====

  /**
   * Search available routers
   */
  searchRouters(
    request: {
      filters?: { field: string; operator: string; value: any }[];
      limit?: number;
      offset?: number;
    } = {},
  ): Observable<{ items: FeedbackRouter[]; total: number }> {
    const body = {
      filters: request.filters || [],
      limit: request.limit ?? 1000000,
      offset: request.offset ?? 0,
    };

    return this.http.post<FeedbackRoutersResponse>(`${this.baseUrl}/feedback-routers/search`, body).pipe(
      map((response) => ({
        items: response.data,
        total: response.total,
      })),
      catchError((error) => {
        console.error('Error searching routers:', error);
        throw error;
      }),
    );
  }

  /**
   * Get all available routers
   */
  getRouters(): Observable<FeedbackRouter[]> {
    return this.searchRouters({ limit: 1000000 }).pipe(map((response) => response.items));
  }

  // ===== Router Assignments API =====

  /**
   * Search router assignments
   */
  searchRouterAssignments(
    request: {
      filters?: { field: string; operator: string; value: any }[];
      limit?: number;
      offset?: number;
    } = {},
  ): Observable<{ items: FeedbackRouterAssignment[]; total: number }> {
    const body = {
      filters: request.filters || [],
      limit: request.limit ?? 1000000,
      offset: request.offset ?? 0,
    };

    return this.http
      .post<FeedbackRouterAssignmentsResponse>(`${this.baseUrl}/feedback-router-assignments/search`, body)
      .pipe(
        map((response) => ({
          items: response.data,
          total: response.total,
        })),
        catchError((error) => {
          console.error('Error searching router assignments:', error);
          throw error;
        }),
      );
  }

  /**
   * Get all router assignments
   */
  getRouterAssignments(): Observable<FeedbackRouterAssignment[]> {
    return this.searchRouterAssignments({ limit: 1000000 }).pipe(map((response) => response.items));
  }

  /**
   * Create a new router assignment
   */
  createRouterAssignment(request: CreateRouterAssignmentRequest): Observable<{ id: string; message: string }> {
    return this.http.post<{ id: string; message: string }>(`${this.baseUrl}/feedback-router-assignments`, request).pipe(
      catchError((error) => {
        console.error('Error creating router assignment:', error);
        throw error;
      }),
    );
  }

  /**
   * Delete a router assignment
   */
  deleteRouterAssignment(id: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/feedback-router-assignments/${id}`).pipe(
      catchError((error) => {
        console.error('Error deleting router assignment:', error);
        throw error;
      }),
    );
  }
}
